import { Link } from "react-router-dom";
import { Logo } from "@/components/Logo";

export default function Privacy() {
  return (
    <div data-testid="privacy-page" className="max-w-3xl mx-auto px-6 py-16 md:py-24 text-cocoa/80 leading-relaxed">
      <Link to="/"><Logo size="md" /></Link>
      <div className="mt-10">
        <div className="text-[10px] tracking-[0.4em] uppercase text-mauve">Legal</div>
        <h1 className="font-heading text-4xl md:text-5xl font-light tracking-tight mt-3 text-cocoa">Privacy Statement</h1>
        <p className="text-[10px] tracking-[0.3em] uppercase text-cocoa/50 mt-3">Last updated: this build · Governed by the Australian Privacy Act 1988 (NSW)</p>
      </div>

      <section className="mt-10 space-y-4 text-sm">
        <h2 className="font-heading text-xl text-cocoa">Our promises</h2>
        <ul className="list-disc pl-5 space-y-2">
          <li><span className="text-mauve font-medium">No emails about your activity.</span> SugarSense will never email you
              about who you read, who you wrote about, your archetype matches, or the
              substance of any advisor read. Activity is surfaced only when you sign in.</li>
          <li><span className="text-mauve font-medium">No advertising.</span> No third-party advertising trackers, no behavioural
              advertising, no remarketing tags, no audience-segment sharing.</li>
          <li><span className="text-mauve font-medium">No sale of data.</span> We never sell or rent your personal data. Period.</li>
          <li><span className="text-mauve font-medium">Anonymisation before AI.</span> Before any of your text touches an AI
              model, identifying details (names, aliases, locations, identifiers) are
              routed through our anonymisation pipeline. The model never sees the
              original names you wrote.</li>
          <li><span className="text-mauve font-medium">Encrypted at rest where it matters.</span> Sensitive credentials such as
              payment-provider tokens are encrypted with industry-standard symmetric
              encryption (AES) before being persisted.</li>
        </ul>

        <h2 className="font-heading text-xl text-cocoa mt-8">What we store</h2>
        <ul className="list-disc pl-5 space-y-2">
          <li>Your email and an irreversibly-hashed password (bcrypt, cost 12).</li>
          <li>An optional display name / alias.</li>
          <li>The substance of any situation you describe to the AI advisor, the
              archetype match the advisor returned, and the next-move draft it
              generated — kept as your private journal.</li>
          <li>An anonymised, de-identified vector of each interaction in our internal
              memory store, used to improve continuity and pattern-recognition across
              your own future reads.</li>
          <li>An audit log of security-relevant events (logins, password resets,
              payment events) retained for 365 days for fraud prevention.</li>
        </ul>

        <h2 className="font-heading text-xl text-cocoa mt-8">How we use what you write</h2>
        <p>
          The content you write into SugarSense is used solely to deliver the features
          you've asked for: the advisor read, the archetype match, your private journal,
          and longitudinal pattern tracking across your own situations. We do not
          surface your content to other users. Anonymised, de-identified data may be
          used to refine the internal behavioural ontology; identifiable content is
          never shared, sold, or used for any other purpose.
        </p>

        <h2 className="font-heading text-xl text-cocoa mt-8">Sub-processors</h2>
        <ul className="list-disc pl-5 space-y-2">
          <li><span className="text-mauve font-medium">Square Inc.</span> — processes membership payments and receives the
              minimum information required to complete a checkout.</li>
          <li><span className="text-mauve font-medium">OpenAI / Anthropic / Google AI</span> — receive only anonymised text
              (no names, no aliases, no contact details) when an AI read is generated.
              They are bound by their own enterprise data-handling commitments and may
              not use this data to train public models.</li>
          <li><span className="text-mauve font-medium">SendGrid</span> — used only for transactional / security emails
              (password reset, login alerts) where enabled by the administrator.</li>
        </ul>

        <h2 className="font-heading text-xl text-cocoa mt-8">Your rights</h2>
        <p>
          Under Australian privacy law, and under equivalent laws in other jurisdictions
          (e.g. GDPR for EU residents), you may request a copy of the personal
          information we hold about you, correct it, restrict its processing, or request
          deletion. The dashboard provides a one-click export of your data and a
          one-click delete-all option. You can also contact the administrator via the
          email address on the deployed instance to exercise these rights manually.
          We will respond substantively within 30 days.
        </p>

        <h2 className="font-heading text-xl text-cocoa mt-8">Security</h2>
        <p>
          We implement industry-standard protections: HTTPS in transit (TLS),
          bcrypt password hashing, symmetric at-rest encryption for sensitive secrets,
          a strict Content Security Policy in production, CSRF double-submit tokens
          on all state-changing requests, rate-limited authentication endpoints,
          structured audit logging, and routine security review. No system is
          perfectly secure; we will notify affected users without undue delay in the
          event of a data breach involving personal information.
        </p>

        <h2 className="font-heading text-xl text-cocoa mt-8">Children</h2>
        <p>
          SugarSense is strictly for users 18+ (or the local age of majority where
          that is higher). We do not knowingly collect personal information from
          minors. Any account belonging to a minor will be terminated and the
          information deleted on notification.
        </p>

        <h2 className="font-heading text-xl text-cocoa mt-8">Jurisdiction</h2>
        <p>
          This statement is governed by the laws of New South Wales, Australia, and
          the Australian Privacy Act 1988 (Cth). For users outside Australia,
          additional rights under your local law (e.g. GDPR for EU users) apply
          where mandatory; we will honour valid requests under those frameworks.
        </p>

        <h2 className="font-heading text-xl text-cocoa mt-8">Contact</h2>
        <p>
          For privacy enquiries, use the <Link to="/contact" className="text-mauve underline">Contact form</Link> on this site.
          We aim to acknowledge requests within 5 business days and respond substantively
          within 30 days.
        </p>
      </section>

      <div className="mt-16 text-center">
        <Link to="/" className="text-[11px] tracking-[0.3em] uppercase text-mauve hover:underline">← Back to SugarSense</Link>
      </div>
    </div>
  );
}
